import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Holiday from "../models/Holiday";
import HolidayService from "../services/HolidayService";
import { QueryKeyFactory } from "../services/queryKeys";
import { unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery } from "../utils/tableQuery";

export const holidayKeys = new QueryKeyFactory(HolidayService.path);
export const HOLIDAY_CONFLICT_MESSAGE = "Holiday with same date and country already created";

function toHolidayConflict(error: unknown): never {
  if (error instanceof ApiError) {
    throw new ApiError(error.errorCode, HOLIDAY_CONFLICT_MESSAGE, error.apiResponse);
  }
  throw error;
}

export const useHolidayList = (country: string) => {
  const queryClient = useQueryClient();
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Holiday>({
        queryKey: (page, pageSize, sortBy, sortDir) => holidayKeys.list({ page, pageSize, sortBy, sortDir, country }),
        queryFn: (page, pageSize) => unwrap(HolidayService.findHolidaysByCountry(page, pageSize, country)),
        client: queryClient,
      }),
    [queryClient, country],
  );

  const refreshTable = () => refreshTableQuery(holidayKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const createMutation = useMutation({
    mutationFn: async (holiday: Holiday) => {
      try {
        return await unwrap(HolidayService.create(holiday));
      } catch (error) {
        toHolidayConflict(error);
      }
    },
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Holiday created!");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (holiday: Holiday) => {
      try {
        return await unwrap(HolidayService.update(holiday));
      } catch (error) {
        toHolidayConflict(error);
      }
    },
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Holiday updated!");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (holiday: Holiday) => unwrap(HolidayService.delete(holiday)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Holiday deleted successfully");
    },
  });

  const handleOnCreate = async (holiday: Holiday) => {
    try {
      await createMutation.mutateAsync(holiday);
      return true;
    } catch {
      return false;
    }
  };

  const handleOnUpdate = async (holiday: Holiday) => {
    try {
      await updateMutation.mutateAsync(holiday);
      return true;
    } catch {
      return false;
    }
  };

  return {
    onDataFetch,
    forceUpdateOn,
    handleOnCreate,
    handleOnUpdate,
    handleOnDelete: (holiday: Holiday) => deleteMutation.mutate(holiday),
  };
};
